import React, { useState, useEffect } from 'react';
import { useMutation } from '@apollo/react-hooks';
import {
  ADD_POSTAL_CODE_TO_MORATORIUM_EVENT,
  REMOVE_POSTAL_CODE_FROM_MORATORIUM_EVENT,
} from 'Admin/Partners/graphql';
import { MultiSelect } from 'react-multi-select-component';
import styles from './styles.module.css';
import {
  isValidPostalCode,
  POSTAL_CODE_ERROR_MESSAGES,
} from './utils/validation';

interface MoratoriumsDetailsPostalCodesProps {
  id: string;
  postalCodes: string[];
}

const MoratoriumsDetailsPostalCodes = ({
  id,
  postalCodes,
}: MoratoriumsDetailsPostalCodesProps) => {
  const [selected, setSelected] = useState<{ label: string; value: string }[]>(
    []
  );
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    // Convert postal codes array to the format expected by MultiSelect
    const formattedPostalCodes = postalCodes.map((code) => ({
      label: code,
      value: code,
    }));
    setSelected(formattedPostalCodes);
  }, [postalCodes]);

  const [addPostalCode, { loading: addingPostalCode }] = useMutation(
    ADD_POSTAL_CODE_TO_MORATORIUM_EVENT
  );
  const [removePostalCode, { loading: removingPostalCode }] = useMutation(
    REMOVE_POSTAL_CODE_FROM_MORATORIUM_EVENT
  );

  const handleChange = async (
    selectedOptions: { label: string; value: string }[]
  ) => {
    const currentPostalCodes = new Set(postalCodes);
    const newPostalCodes = new Set(
      selectedOptions.map((option) => option.value)
    );

    // Find postal codes to add (present in new but not in current)
    const postalCodesToAdd = Array.from(newPostalCodes).filter(
      (code) => !currentPostalCodes.has(code)
    );

    // Find postal codes to remove (present in current but not in new)
    const postalCodesToRemove = Array.from(currentPostalCodes).filter(
      (code) => !newPostalCodes.has(code)
    );

    // Add new postal codes
    for (const code of postalCodesToAdd) {
      try {
        await addPostalCode({
          variables: {
            id,
            postalCode: code,
          },
        });
      } catch (error) {
        console.error('Error adding postal code:', error);
      }
    }

    // Remove postal codes
    for (const code of postalCodesToRemove) {
      try {
        await removePostalCode({
          variables: {
            id,
            postalCode: code,
          },
        });
      } catch (error) {
        console.error('Error removing postal code:', error);
      }
    }

    setSelected(selectedOptions);
  };

  const handleCreateOption = (inputValue: string) => {
    setError(null);
    const trimmedValue = inputValue.trim();

    if (!isValidPostalCode(trimmedValue)) {
      setError(POSTAL_CODE_ERROR_MESSAGES.INVALID_FORMAT);
      return null;
    }

    return {
      label: trimmedValue,
      value: trimmedValue,
    };
  };

  const options = selected.map((code) => ({
    label: code.value,
    value: code.value,
  }));

  return (
    <div className={styles.postalCodesContainer}>
      <h3>Postal Codes</h3>
      <MultiSelect
        options={options}
        value={selected}
        onChange={handleChange}
        labelledBy="Select postal codes"
        isCreatable={true}
        hasSelectAll={false}
        disabled={addingPostalCode || removingPostalCode}
        valueRenderer={(selected, _options) => {
          return selected.length
            ? selected.map(({ label }) => label).join(', ')
            : 'No Items Selected';
        }}
        onCreateOption={handleCreateOption}
      />
      {error && <div className={styles.error}>{error}</div>}
    </div>
  );
};

export default MoratoriumsDetailsPostalCodes;
